// pkg125 — admin prop: Client Modules workspace renders this page; full CRUD via admin asset-ledger endpoints; Submit hidden in admin mode (D-124e); download via admin endpoint — delivered 2026-07-27
// pkg72b — the page's private blue accent #1f5fa5 (buttons, pills, links, progress) → NAVY #1E2761; ✓-Submitted greens kept — delivered 2026-07-12
// pkg31 — client can download their own list as the Word doc (Download Word file button)
// pkg26 — Asset & Liability Ledger client page (input side).
//
// Wired to the pkg25 endpoints (/api/asset-ledger/*). Categories, sub-type
// dropdowns, classification buckets, and the "when acquired" options are all
// driven by the taxonomy the server returns (GET /taxonomy) so this page stays
// in sync with the curated list — and any sub-types a firm adds — without a
// frontend change. Interaction mirrors the approved prototype: collapsed
// categories, a focused add/edit form, classification + when-acquired per row,
// progressive disclosure, and submit. Attorney-only columns are never shown.

import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../utils/api';
import { adminApi } from '../utils/adminApi'; // pkg125 — admin mode

const POSSESSION = ['Husband', 'Wife', 'Shared'];
const FIRST_N = 4; // categories shown before "show all"

function money(v) {
  if (v === '' || v === null || v === undefined) return '';
  const n = parseFloat(v);
  if (isNaN(n)) return '';
  return '$' + n.toLocaleString('en-US');
}
function num(v) {
  if (v === '' || v === null || v === undefined) return null;
  const n = parseFloat(v);
  return isNaN(n) ? null : n;
}

// ── styles (portal-native: Arial, flat, simple borders) ──────────────────────
const S = {
  card:    { border: '1px solid #e2e2e2', borderRadius: 6, marginBottom: 10, background: '#fff', overflow: 'hidden' },
  row:     { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '13px 15px', cursor: 'pointer' },
  catName: { fontSize: 15 },
  count:   { fontSize: 13, color: '#666' },
  add:     { fontSize: 13, color: '#1E2761', fontWeight: 500 },
  item:    { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 15px', borderTop: '1px solid #eee' },
  meta:    { fontSize: 12, color: '#777', marginTop: 2 },
  chip:    { fontSize: 11, padding: '2px 8px', borderRadius: 4, background: '#eaf1fb', color: '#1E2761', whiteSpace: 'nowrap' },
  iconBtn: { background: 'none', border: 'none', color: '#999', cursor: 'pointer', fontSize: 13, padding: '2px 6px', textDecoration: 'underline' },
  form:    { borderTop: '1px solid #d8d8d8', padding: '14px 15px', background: '#fafafa' },
  fl:      { fontSize: 12, color: '#666', display: 'block', marginBottom: 4 },
  fg:      { marginBottom: 11 },
  input:   { width: '100%', padding: '7px 8px', border: '1px solid #ccc', borderRadius: 4, fontSize: 13, fontFamily: 'Arial, sans-serif', boxSizing: 'border-box' },
  sec:     { fontSize: 13, fontWeight: 600, color: '#444', margin: '20px 2px 8px' },
  pill:    { fontSize: 12, padding: '6px 12px', borderRadius: 4, border: '1px solid #ccc', background: '#fff', color: '#555', cursor: 'pointer' },
  pillOn:  { fontSize: 12, padding: '6px 12px', borderRadius: 4, border: '1px solid #1E2761', background: '#1E2761', color: '#fff', cursor: 'pointer' },
  primary: { background: '#1E2761', color: '#fff', border: 'none', borderRadius: 4, padding: '8px 16px', fontSize: 13, cursor: 'pointer' },
  ghost:   { background: '#fff', color: '#555', border: '1px solid #ccc', borderRadius: 4, padding: '8px 16px', fontSize: 13, cursor: 'pointer' },
  showAll: { textAlign: 'center', fontSize: 13, color: '#1E2761', padding: 6, cursor: 'pointer' },
};

// pkg125 — `admin` prop ({ clientId }): rendered by the Client Modules
// workspace. All CRUD goes through the /api/admin/clients/:id/asset-ledger
// endpoints (same column whitelists server-side); Submit hidden (D-124e);
// Word download via the admin endpoint. The ✓ Submitted chip still shows the
// client's status.
export default function AssetLedgerPage({ admin = null }) {
  const io = admin ? {
    taxonomy:        () => adminApi.getClientLedgerTaxonomy(admin.clientId),
    get:             () => adminApi.getClientAssetLedger(admin.clientId),
    addAsset:        (p) => adminApi.addClientAsset(admin.clientId, p),
    updateAsset:     (id, p) => adminApi.updateClientAsset(admin.clientId, id, p),
    deleteAsset:     (id) => adminApi.deleteClientAsset(admin.clientId, id),
    addLiability:    (p) => adminApi.addClientLiability(admin.clientId, p),
    updateLiability: (id, p) => adminApi.updateClientLiability(admin.clientId, id, p),
    deleteLiability: (id) => adminApi.deleteClientLiability(admin.clientId, id),
    download:        () => adminApi.downloadClientAssetLedger(admin.clientId),
  } : {
    taxonomy:        () => api.getAssetLedgerTaxonomy(),
    get:             () => api.getAssetLedger(),
    addAsset:        (p) => api.addAsset(p),
    updateAsset:     (id, p) => api.updateAsset(id, p),
    deleteAsset:     (id) => api.deleteAsset(id),
    addLiability:    (p) => api.addLiability(p),
    updateLiability: (id, p) => api.updateLiability(id, p),
    deleteLiability: (id) => api.deleteLiability(id),
    download:        () => api.downloadAssetLedger(),
  };
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [status, setStatus]     = useState('');
  const [tax, setTax]           = useState(null);
  const [assets, setAssets]     = useState([]);
  const [liabilities, setLiab]  = useState([]);
  const [expanded, setExpanded] = useState({});
  const [form, setForm]         = useState(null); // { kind, categoryKey, editId, fields }
  const [showAllA, setShowAllA] = useState(false);
  const [showAllL, setShowAllL] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted]   = useState(false);

  const load = useCallback(async () => {
    try {
      const [taxonomy, ledger] = await Promise.all([
        io.taxonomy(),
        io.get(),
      ]);
      setTax(taxonomy);
      setAssets(ledger.assets || []);
      setLiab(ledger.liabilities || []);
      setSubmitted(ledger.ledger && ledger.ledger.status === 'submitted');
    } catch (err) {
      setError(err.message || 'Could not load your asset list.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div style={{ padding: 24, fontFamily: 'Arial' }}>Loading…</div>;
  if (error && !tax) return <div style={{ padding: 24, fontFamily: 'Arial', color: '#b00' }}>{error}</div>;

  const classifications = tax.classifications || [];
  const whenOptions     = tax.when_acquired || [];
  const clsLabel = (k) => (classifications.find(c => c.key === k) || {}).label || k;

  const listFor = (kind, catKey) =>
    (kind === 'asset' ? assets : liabilities).filter(it => it.category_key === catKey);

  const startedCount = new Set(
    [...assets, ...liabilities].map(it => it.category_key)
  ).size;
  const totalCats = (tax.assets.length + tax.liabilities.length);
  const pct = totalCats ? Math.round((startedCount / totalCats) * 100) : 0;
  const totalItems = assets.length + liabilities.length;

  function openAdd(kind, categoryKey) {
    setForm({ kind, categoryKey, editId: null, fields: { classification: 'marital', when: whenOptions[0] ? whenOptions[0].key : '' } });
    setExpanded(p => ({ ...p, [categoryKey]: true }));
    setStatus('');
  }
  function openEdit(kind, it) {
    const fields = kind === 'asset'
      ? { subtype_key: it.subtype_key || '', description: it.description || '', classification: it.classification || 'marital',
          when: it.when_acquired || '', husband_value: it.husband_value ?? '', wife_value: it.wife_value ?? '', possession: it.possession || '' }
      : { subtype_key: it.subtype_key || '', creditor: it.creditor || '', classification: it.classification || 'marital',
          when: it.when_incurred || '', total_balance: it.total_balance ?? '', monthly_payment: it.monthly_payment ?? '' };
    setForm({ kind, categoryKey: it.category_key, editId: it.id, fields });
    setExpanded(p => ({ ...p, [it.category_key]: true }));
    setStatus('');
  }
  function setField(name, value) {
    setForm(f => ({ ...f, fields: { ...f.fields, [name]: value } }));
  }

  async function saveForm() {
    const { kind, categoryKey, editId, fields } = form;
    setStatus(''); setError('');
    const payload = kind === 'asset'
      ? { category_key: categoryKey, subtype_key: fields.subtype_key || null, classification: fields.classification || null,
          description: fields.description || null, when_acquired: fields.when || null,
          husband_value: num(fields.husband_value), wife_value: num(fields.wife_value), possession: fields.possession || null }
      : { category_key: categoryKey, subtype_key: fields.subtype_key || null, classification: fields.classification || null,
          creditor: fields.creditor || null, when_incurred: fields.when || null,
          total_balance: num(fields.total_balance), monthly_payment: num(fields.monthly_payment) };
    try {
      if (kind === 'asset') {
        const saved = editId ? await io.updateAsset(editId, payload) : await io.addAsset(payload);
        setAssets(prev => editId ? prev.map(x => x.id === editId ? saved : x) : [...prev, saved]);
      } else {
        const saved = editId ? await io.updateLiability(editId, payload) : await io.addLiability(payload);
        setLiab(prev => editId ? prev.map(x => x.id === editId ? saved : x) : [...prev, saved]);
      }
      setForm(null);
    } catch (err) {
      setError(err.message || 'Could not save.');
    }
  }

  async function deleteItem(kind, id) {
    setStatus(''); setError('');
    try {
      if (kind === 'asset') { await io.deleteAsset(id); setAssets(prev => prev.filter(x => x.id !== id)); }
      else { await io.deleteLiability(id); setLiab(prev => prev.filter(x => x.id !== id)); }
    } catch (err) { setError(err.message || 'Could not delete.'); }
  }

  async function submit() {
    setSubmitting(true); setStatus(''); setError('');
    try { await api.submitAssetLedger(); setSubmitted(true); setStatus('Submitted to your attorney.'); }
    catch (err) { setError(err.message || 'Could not submit.'); }
    finally { setSubmitting(false); }
  }

  async function downloadDoc() {
    setError('');
    try {
      const blob = await io.download();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Asset_Debt_List.docx';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) { setError(err.message || 'Could not download the file.'); }
  }

  function subtypeLabel(cat, key) {
    const st = (cat.subtypes || []).find(s => s.key === key);
    return st ? st.label : (key || '');
  }

  function renderItem(kind, cat, it) {
    const title = kind === 'asset' ? (it.description || subtypeLabel(cat, it.subtype_key)) : (it.creditor || subtypeLabel(cat, it.subtype_key));
    const meta = kind === 'asset'
      ? [money(it.husband_value) && 'H ' + money(it.husband_value), money(it.wife_value) && 'W ' + money(it.wife_value), it.possession && it.possession + ' has it'].filter(Boolean).join(' · ') || 'No value yet'
      : [money(it.total_balance), money(it.monthly_payment) && money(it.monthly_payment) + '/mo'].filter(Boolean).join(' · ') || 'No balance yet';
    return (
      <div key={it.id} style={S.item}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 13 }}>{title}</div>
          <div style={S.meta}>{meta}</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={S.chip}>{clsLabel(it.classification)}</span>
          <button style={S.iconBtn} onClick={() => openEdit(kind, it)}>Edit</button>
          <button style={S.iconBtn} onClick={() => deleteItem(kind, it.id)}>Delete</button>
        </div>
      </div>
    );
  }

  function renderForm(kind, cat) {
    const f = form.fields;
    return (
      <div style={S.form}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>
          {form.editId ? 'Edit' : 'Add to ' + cat.label}
        </div>

        <div style={S.fg}>
          <label style={S.fl}>Type</label>
          <select style={S.input} value={f.subtype_key || ''} onChange={e => setField('subtype_key', e.target.value)}>
            <option value="">Select…</option>
            {(cat.subtypes || []).map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
        </div>

        {kind === 'asset' ? (
          <div style={S.fg}>
            <label style={S.fl}>Description</label>
            <input style={S.input} type="text" value={f.description || ''} onChange={e => setField('description', e.target.value)} placeholder="e.g. 2019 Ford F-250" />
          </div>
        ) : (
          <div style={S.fg}>
            <label style={S.fl}>Creditor</label>
            <input style={S.input} type="text" value={f.creditor || ''} onChange={e => setField('creditor', e.target.value)} placeholder="e.g. Capital One" />
          </div>
        )}

        <div style={S.fg}>
          <label style={S.fl}>{kind === 'asset' ? 'This belongs to' : 'Whose debt'}</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {classifications.map(c => (
              <button key={c.key} style={f.classification === c.key ? S.pillOn : S.pill} onClick={() => setField('classification', c.key)}>{c.label}</button>
            ))}
          </div>
        </div>

        <div style={S.fg}>
          <label style={S.fl}>{kind === 'asset' ? 'When acquired' : 'When incurred'}</label>
          <select style={S.input} value={f.when || ''} onChange={e => setField('when', e.target.value)}>
            {whenOptions.map(w => <option key={w.key} value={w.key}>{w.label}</option>)}
          </select>
        </div>

        {kind === 'asset' ? (
          <>
            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ ...S.fg, flex: 1 }}>
                <label style={S.fl}>Husband&rsquo;s value</label>
                <input style={S.input} type="number" min="0" step="0.01" value={f.husband_value ?? ''} onChange={e => setField('husband_value', e.target.value)} placeholder="$" />
              </div>
              <div style={{ ...S.fg, flex: 1 }}>
                <label style={S.fl}>Wife&rsquo;s value</label>
                <input style={S.input} type="number" min="0" step="0.01" value={f.wife_value ?? ''} onChange={e => setField('wife_value', e.target.value)} placeholder="$" />
              </div>
            </div>
            <div style={S.fg}>
              <label style={S.fl}>Who has it now</label>
              <select style={S.input} value={f.possession || ''} onChange={e => setField('possession', e.target.value)}>
                <option value="">Select…</option>
                {POSSESSION.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
          </>
        ) : (
          <div style={{ display: 'flex', gap: 10 }}>
            <div style={{ ...S.fg, flex: 1 }}>
              <label style={S.fl}>Total balance</label>
              <input style={S.input} type="number" min="0" step="0.01" value={f.total_balance ?? ''} onChange={e => setField('total_balance', e.target.value)} placeholder="$" />
            </div>
            <div style={{ ...S.fg, flex: 1 }}>
              <label style={S.fl}>Monthly payment</label>
              <input style={S.input} type="number" min="0" step="0.01" value={f.monthly_payment ?? ''} onChange={e => setField('monthly_payment', e.target.value)} placeholder="$" />
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
          <button style={S.primary} onClick={saveForm}>Save</button>
          <button style={S.ghost} onClick={() => setForm(null)}>Cancel</button>
        </div>
      </div>
    );
  }

  function renderCard(kind, cat) {
    const list = listFor(kind, cat.key);
    const isOpen = !!expanded[cat.key];
    const formHere = form && form.kind === kind && form.categoryKey === cat.key;
    return (
      <div key={cat.key} style={S.card}>
        <div style={S.row} onClick={() => setExpanded(p => ({ ...p, [cat.key]: !p[cat.key] }))}>
          <span style={S.catName}>{cat.label}</span>
          {list.length
            ? <span style={S.count}>{list.length} {list.length > 1 ? 'items' : 'item'} {isOpen ? '▲' : '▾'}</span>
            : <span style={S.add}>+ Add</span>}
        </div>
        {isOpen && list.map(it => renderItem(kind, cat, it))}
        {isOpen && (formHere
          ? renderForm(kind, cat)
          : <div style={{ padding: '10px 15px', borderTop: '1px solid #eee' }}>
              <button style={S.ghost} onClick={() => openAdd(kind, cat.key)}>+ Add {cat.label.toLowerCase()}</button>
            </div>)}
      </div>
    );
  }

  const assetCats = showAllA ? tax.assets : tax.assets.slice(0, FIRST_N);
  const liabCats  = showAllL ? tax.liabilities : tax.liabilities.slice(0, FIRST_N);

  return (
    <div style={{ maxWidth: 720, margin: '0 auto', padding: '8px 4px', fontFamily: 'Arial, sans-serif' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 20 }}>Assets &amp; debts</h2>
          <div style={{ fontSize: 13, color: '#666', marginTop: 2 }}>Your property and what&rsquo;s owed</div>
        </div>
        {submitted && <span style={{ fontSize: 12, color: '#2e7d32' }}>✓ Submitted</span>}
      </div>

      <div style={{ height: 6, background: '#eee', borderRadius: 99, overflow: 'hidden', marginBottom: 7 }}>
        <div style={{ width: pct + '%', height: '100%', background: '#1E2761' }} />
      </div>
      <div style={{ fontSize: 12, color: '#999', marginBottom: 4 }}>
        Add only what applies — skip the rest. You can leave and come back anytime.
      </div>

      {error && <div style={{ color: '#b00', fontSize: 13, margin: '8px 0' }}>{error}</div>}
      {status && <div style={{ color: '#2e7d32', fontSize: 13, margin: '8px 0' }}>{status}</div>}

      <div style={S.sec}>Assets</div>
      {assetCats.map(cat => renderCard('asset', cat))}
      {tax.assets.length > FIRST_N && (
        <div style={S.showAll} onClick={() => setShowAllA(v => !v)}>
          {showAllA ? 'Show fewer' : `Show all ${tax.assets.length} asset categories`}
        </div>
      )}

      <div style={S.sec}>Debts</div>
      {liabCats.map(cat => renderCard('liability', cat))}
      {tax.liabilities.length > FIRST_N && (
        <div style={S.showAll} onClick={() => setShowAllL(v => !v)}>
          {showAllL ? 'Show fewer' : `Show all ${tax.liabilities.length} debt categories`}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16, paddingTop: 14, borderTop: '1px solid #ddd' }}>
        <span style={{ fontSize: 13, color: '#666' }}>{totalItems} item{totalItems === 1 ? '' : 's'} entered</span>
        <div style={{ display: 'flex', gap: 8 }}>
          <button style={S.ghost} onClick={downloadDoc}>Download Word file</button>
          {!admin && (
            <button style={{ ...S.primary, opacity: submitting ? 0.6 : 1 }} disabled={submitting} onClick={submit}>
              {submitting ? 'Submitting…' : 'Submit to your attorney'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
